"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  UsersRound,
} from "lucide-react";

import { ServiceIconBadge } from "@/components/services/service-icon-badge";
import { resolveServiceCoverUrl } from "@/lib/services/covers";
import { IntroductionChecklist } from "@/components/onboarding/introduction-checklist";
import { useCurrentUser, useMyCommunities } from "@/hooks/use-current-user";
import { useUpcomingEvents } from "@/hooks/use-events";
import { useMyPracticeMemberships } from "@/hooks/use-practices";
import { useCatalogServices } from "@/hooks/use-services";
import { EMPTY_EVENT_FILTERS, formatEventWhen } from "@/lib/events/types";
import { createId } from "@/lib/utils";

function SectionCard({
  title,
  actionLabel,
  actionHref,
  children,
}: {
  title: string;
  actionLabel?: string;
  actionHref?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="shadow-surface rounded-2xl border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div className="flex min-w-0 items-center">
          <h2 className="whitespace-nowrap text-sm font-bold text-slate-900">{title}</h2>
        </div>
        {actionLabel && actionHref ? (
          <Link
            href={actionHref}
            className="inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-primary transition-colors hover:text-primary/80"
          >
            {actionLabel}
            <ChevronRight className="size-4" />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function formatMemberCount(count?: number) {
  if (!count) {
    return null;
  }
  if (count >= 1000) {
    return `${(count / 1000).toLocaleString("hu-HU", { maximumFractionDigits: 1 })} E tag`;
  }
  return `${count} tag`;
}

function recommendationIndex(seed: string, count: number) {
  let hash = 0;
  for (const character of seed) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return count ? hash % count : 0;
}

export function RightSidebar() {
  const [recommendationSeed] = useState(() => createId());
  const currentUserQuery = useCurrentUser();
  const enabled = currentUserQuery.data?.accountStatus === "active";
  const communitiesQuery = useMyCommunities(enabled);
  const eventsQuery = useUpcomingEvents(EMPTY_EVENT_FILTERS, enabled);
  const servicesQuery = useCatalogServices(enabled);
  const practicesQuery = useMyPracticeMemberships(Boolean(enabled));
  const communities = communitiesQuery.data ?? [];
  const upcomingEvents = (eventsQuery.data ?? []).slice(0, 3);
  const belongsToPractice = (practicesQuery.data ?? []).some(
    (practice) => practice.membershipStatus === "active",
  );
  const introductionOpen = Boolean(
    enabled &&
      currentUserQuery.data?.onboardingCompleted &&
      !currentUserQuery.data.introductionSkipped &&
      !currentUserQuery.data.introductionExpired &&
      !(
        belongsToPractice &&
        communities.length > 0 &&
        currentUserQuery.data.introductionServicesViewed &&
        currentUserQuery.data.introductionSettingsCompleted
      ),
  );
  const recommendedService = useMemo(() => {
    const services = servicesQuery.data ?? [];
    return services.length
      ? services[recommendationIndex(recommendationSeed, services.length)]
      : null;
  }, [recommendationSeed, servicesQuery.data]);

  return (
    <aside className="h-full overflow-y-auto py-6 pr-6 pl-2">
      <div className="min-w-0 space-y-4">
          <IntroductionChecklist
            enabled={Boolean(enabled)}
            communityCount={communities.length}
          />

          {!introductionOpen ? (
            <>
          <SectionCard
            title="Közösségeim"
            actionLabel="Összes"
            actionHref="/communities"
          >
            {communitiesQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Betöltés...</p>
            ) : communities.length === 0 ? (
              <p className="text-xs leading-relaxed text-muted-foreground">
                Még nem tartozik aktív közösséghez.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {communities.slice(0, 4).map((community) => (
                  <li key={community.id}>
                    <Link
                      href={`/communities/${community.slug}`}
                      className="group flex items-center gap-3 py-3 transition-colors hover:text-primary"
                    >
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                        <UsersRound className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {community.name}
                        </p>
                        {formatMemberCount(community.memberCount) ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatMemberCount(community.memberCount)}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRight className="size-5 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            title="Közelgő események"
            actionLabel="Összes"
            actionHref="/events"
          >
            {eventsQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Betöltés...</p>
            ) : upcomingEvents.length === 0 ? (
              <div className="relative mt-3 flex min-h-16 items-center gap-3 overflow-hidden rounded-xl bg-orange-50/70 p-3 text-xs leading-relaxed text-muted-foreground">
                <CalendarDays className="size-5 shrink-0 text-orange-500" />
                Jelenleg nincs közelgő esemény.
                <CalendarDays className="absolute -right-2 -bottom-3 size-16 rotate-[-8deg] text-orange-100" />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcomingEvents.map((event) => (
                  <li key={event.id}>
                    <Link
                      href={`/events/${event.slug}`}
                      className="group flex items-center gap-3 py-3 transition-colors hover:text-primary"
                    >
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-500">
                        <CalendarDays className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {event.title}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {formatEventWhen(event.startsAt, event.timezone)}
                        </p>
                      </div>
                      <ChevronRight className="size-5 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
            </>
          ) : null}

          {recommendedService ? (
            <Link
              href={`/services/${recommendedService.slug}`}
              className="shadow-surface group block overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-indigo-50/60 to-white transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md"
            >
                <div className="relative h-24 overflow-hidden bg-gradient-to-br from-blue-100 via-indigo-100 to-violet-100">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={resolveServiceCoverUrl(
                      recommendedService.coverUrl,
                      recommendedService.category.slug,
                    )}
                    alt=""
                    className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/90 px-2 py-1 text-[10px] font-semibold text-blue-700 shadow-sm">
                    <Image
                      src="/brand/praxistars-mark.png?v=2"
                      alt=""
                      width={12}
                      height={12}
                      className="size-3 object-contain"
                    />
                    Ajánlott
                  </span>
                  <span className="absolute right-2 bottom-2 max-w-[80%] truncate rounded-full bg-slate-950/65 px-2 py-1 text-[9px] font-medium text-white backdrop-blur-sm">
                    {recommendedService.category.name}
                  </span>
                </div>

                <div className="relative px-3 pt-7 pb-3">
                  <ServiceIconBadge
                    iconName={recommendedService.iconName}
                    iconColor={recommendedService.iconColor}
                    categorySlug={recommendedService.category.slug}
                    className="absolute -top-6 left-3 size-12 rounded-full border-4 border-white !bg-white shadow-sm"
                    iconClassName="size-5"
                  />
                  <h3 className="line-clamp-2 text-sm font-bold leading-snug text-slate-900">
                    {recommendedService.name}
                  </h3>
                  <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-slate-600">
                    {recommendedService.summary}
                  </p>
                  <span className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-sm transition-colors group-hover:bg-primary/90">
                    Részletek
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
            </Link>
          ) : null}
      </div>
    </aside>
  );
}
